import { useContext, useEffect, useState } from 'react'
import { ThemeContext } from '@vibialighting/ui-kit'
import Toggles from '@domain/toggles/Toggles'

import { DEFAULT_THEME_PREFERENCE } from '@/constants/ui.constants'
import { getThemeCookie, setThemeCookie } from '@/utils'

// Clave legacy: usuarios previos guardaban la preferencia (light/dark/system) en localStorage.
// Solo se lee como fallback de migración cuando aún no hay cookie compartida.
const LEGACY_STORAGE_KEY = 'vibia_theme'
const LEGACY_TO_PREFERENCE = { light: 'white', dark: 'black', system: 'system' }

// Preferencia = valor crudo de la cookie `theme_vibia`, espejo de la professional-area nueva.
// `labelKey` resuelve el texto por i18n; `label` queda como default EN (fallback hasta que la clave
// aterrice en masters), mismo patrón que titleKey/titleDefault del sidebar.
const CYCLE = [
	{ value: 'white', icon: 'sun', label: 'Light', labelKey: 'header.professionalShell.themeOption.light' },
	{ value: 'black', icon: 'moon', label: 'Dark', labelKey: 'header.professionalShell.themeOption.dark' },
	{ value: 'system', icon: 'circle-half-tilt-fill', label: 'System', labelKey: 'header.professionalShell.themeOption.system' }
]

const readLegacyPreference = () => {
	try {
		return LEGACY_TO_PREFERENCE[window.localStorage.getItem(LEGACY_STORAGE_KEY)] || null
	} catch (e) { /* localStorage no disponible */ }
	return null
}

const readPreference = () => getThemeCookie() || readLegacyPreference() || DEFAULT_THEME_PREFERENCE

const prefersDark = () => !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches

// Traduce la preferencia al tema que entiende el ThemeContext (light/dark). Espejo de resolveDataTheme.
const resolveTheme = (preference) => {
	if (preference === 'system') return prefersDark() ? 'dark' : 'light'
	return preference === 'black' ? 'dark' : 'light'
}

// Lógica de preferencia de tema (cookie `theme_vibia` + aplicación al ThemeContext).
// Debe invocarse desde un punto SIEMPRE montado (el shell), no desde el control, para que el
// tema se aplique aunque el menú esté cerrado o no haya sesión. Devuelve { current, cycle } para
// pintar el control allá donde viva (hoy, un item del menú de cuenta).
const useThemePreference = () => {
	const { setTheme } = useContext(ThemeContext)
	const [preference, setPreference] = useState(readPreference)
	// Selector de tema gateado: apagado → se fuerza claro (ignora cookie/SO) y el control se oculta
	// (devolvemos current: null, que el menú de cuenta ya usa como guard).
	const enabled = Toggles.isEnabled('enableThemeSelector')

	// Si llegamos sin cookie (default o migración del localStorage legacy), la publicamos para que
	// eproject exponga su tema en la cookie compartida desde el primer render.
	useEffect(() => {
		if (enabled && !getThemeCookie()) setThemeCookie(preference)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!enabled) {
			setTheme('light')
			return undefined
		}
		const apply = () => setTheme(resolveTheme(preference))
		apply()
		if (preference !== 'system' || !window.matchMedia) return undefined
		const mq = window.matchMedia('(prefers-color-scheme: dark)')
		const handler = () => apply()
		mq.addEventListener('change', handler)
		return () => mq.removeEventListener('change', handler)
	}, [preference, setTheme, enabled])

	if (!enabled) return { current: null, cycle: () => {} }

	const current = CYCLE.find((option) => option.value === preference) || CYCLE[0]

	const cycle = () => {
		const index = CYCLE.findIndex((option) => option.value === preference)
		const next = CYCLE[(index + 1) % CYCLE.length].value
		setPreference(next)
		setThemeCookie(next)
	}

	return { current, cycle }
}

export default useThemePreference
